import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Save, Settings, Zap, Trophy, Flame, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface GamificationSettingsProps {
  companyId: string;
  settings: any;
  onSettingsUpdated: () => void;
}

interface SettingsFormData {
  gamification_title: string;
  welcome_message: string;
  cpd_points_per_simulation: number;
  cpd_points_goal_bonus: number;
  cpd_points_perfect_score: number;
  cpd_monthly_target: number;
  streak_bonus_enabled: boolean;
  streak_7_day_bonus: number;
  streak_14_day_bonus: number;
  streak_30_day_bonus: number;
  leaderboard_enabled: boolean;
  badges_enabled: boolean;
  milestones_enabled: boolean;
  rewards_enabled: boolean;
  // Coaching CPD settings
  cpd_points_per_coaching: number;
  cpd_points_coaching_excellence: number;
  cpd_points_coaching_exceptional: number;
  coaching_badges_enabled: boolean;
}

const defaultSettings: SettingsFormData = {
  gamification_title: 'Achievement Center',
  welcome_message: 'Track your progress and earn rewards!',
  cpd_points_per_simulation: 5,
  cpd_points_goal_bonus: 10,
  cpd_points_perfect_score: 15,
  cpd_monthly_target: 60,
  streak_bonus_enabled: true,
  streak_7_day_bonus: 25,
  streak_14_day_bonus: 50,
  streak_30_day_bonus: 100,
  leaderboard_enabled: true,
  badges_enabled: true,
  milestones_enabled: true,
  rewards_enabled: true,
  // Coaching defaults
  cpd_points_per_coaching: 8,
  cpd_points_coaching_excellence: 5,
  cpd_points_coaching_exceptional: 5,
  coaching_badges_enabled: true,
};

export function GamificationSettings({ companyId, settings, onSettingsUpdated }: GamificationSettingsProps) {
  const [formData, setFormData] = useState<SettingsFormData>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with existing settings
  useEffect(() => {
    if (settings) {
      setFormData({
        gamification_title: settings.gamification_title || defaultSettings.gamification_title,
        welcome_message: settings.welcome_message || defaultSettings.welcome_message,
        cpd_points_per_simulation: settings.cpd_points_per_simulation ?? defaultSettings.cpd_points_per_simulation,
        cpd_points_goal_bonus: settings.cpd_points_goal_bonus ?? defaultSettings.cpd_points_goal_bonus,
        cpd_points_perfect_score: settings.cpd_points_perfect_score ?? defaultSettings.cpd_points_perfect_score,
        cpd_monthly_target: settings.cpd_monthly_target ?? defaultSettings.cpd_monthly_target,
        streak_bonus_enabled: settings.streak_bonus_enabled ?? defaultSettings.streak_bonus_enabled,
        streak_7_day_bonus: settings.streak_7_day_bonus ?? defaultSettings.streak_7_day_bonus,
        streak_14_day_bonus: settings.streak_14_day_bonus ?? defaultSettings.streak_14_day_bonus,
        streak_30_day_bonus: settings.streak_30_day_bonus ?? defaultSettings.streak_30_day_bonus,
        leaderboard_enabled: settings.leaderboard_enabled ?? defaultSettings.leaderboard_enabled,
        badges_enabled: settings.badges_enabled ?? defaultSettings.badges_enabled,
        milestones_enabled: settings.milestones_enabled ?? defaultSettings.milestones_enabled,
        rewards_enabled: settings.rewards_enabled ?? defaultSettings.rewards_enabled,
        // Coaching settings
        cpd_points_per_coaching: settings.cpd_points_per_coaching ?? defaultSettings.cpd_points_per_coaching,
        cpd_points_coaching_excellence: settings.cpd_points_coaching_excellence ?? defaultSettings.cpd_points_coaching_excellence,
        cpd_points_coaching_exceptional: settings.cpd_points_coaching_exceptional ?? defaultSettings.cpd_points_coaching_exceptional,
        coaching_badges_enabled: settings.coaching_badges_enabled ?? defaultSettings.coaching_badges_enabled,
      });
    }
  }, [settings]);

  const updateField = (field: keyof SettingsFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (settings?.id) {
        // Update existing
        const { error } = await supabase
          .from('company_gamification_settings')
          .update(formData)
          .eq('id', settings.id);
        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('company_gamification_settings')
          .insert({
            company_id: companyId,
            ...formData,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Settings saved successfully');
      setHasChanges(false);
      onSettingsUpdated();
    },
    onError: (error: any) => {
      toast.error('Failed to save settings: ' + error.message);
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Gamification Settings</h2>
          <p className="text-muted-foreground">Configure how your team earns and displays achievements</p>
        </div>
        <Button 
          onClick={() => saveMutation.mutate()} 
          disabled={!hasChanges || saveMutation.isPending}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Branding */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Branding
          </CardTitle>
          <CardDescription>Customize how gamification appears to your team</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Dashboard Title</Label>
            <Input
              value={formData.gamification_title}
              onChange={(e) => updateField('gamification_title', e.target.value)}
              placeholder="Achievement Center"
            />
          </div>
          <div>
            <Label>Welcome Message</Label>
            <Input
              value={formData.welcome_message}
              onChange={(e) => updateField('welcome_message', e.target.value)}
              placeholder="Track your progress and earn rewards!"
            />
          </div>
        </CardContent>
      </Card>

      {/* CPD Points Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            CPD Points Configuration
          </CardTitle>
          <CardDescription>Set how many points are awarded for different achievements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Points per Simulation Completion</Label>
              <Input
                type="number"
                value={formData.cpd_points_per_simulation}
                onChange={(e) => updateField('cpd_points_per_simulation', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Base points for completing any simulation</p>
            </div>
            <div>
              <Label>Goal Achievement Bonus</Label>
              <Input
                type="number"
                value={formData.cpd_points_goal_bonus}
                onChange={(e) => updateField('cpd_points_goal_bonus', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Extra points for reaching the goal score</p>
            </div>
            <div>
              <Label>Perfect Score Bonus</Label>
              <Input
                type="number"
                value={formData.cpd_points_perfect_score}
                onChange={(e) => updateField('cpd_points_perfect_score', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Bonus for 100% score</p>
            </div>
            <div>
              <Label>Monthly CPD Target</Label>
              <Input
                type="number"
                value={formData.cpd_monthly_target}
                onChange={(e) => updateField('cpd_monthly_target', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Target points per user per month</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coaching CPD Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Coaching CPD Configuration
              </CardTitle>
              <CardDescription>Set points for coaching session completions</CardDescription>
            </div>
            <Switch
              checked={formData.coaching_badges_enabled}
              onCheckedChange={(checked) => updateField('coaching_badges_enabled', checked)}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label>Points per Coaching Session</Label>
              <Input
                type="number"
                value={formData.cpd_points_per_coaching}
                onChange={(e) => updateField('cpd_points_per_coaching', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Base points for completing coaching</p>
            </div>
            <div>
              <Label>Excellence Bonus (80%+ Readiness)</Label>
              <Input
                type="number"
                value={formData.cpd_points_coaching_excellence}
                onChange={(e) => updateField('cpd_points_coaching_excellence', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Bonus for 80%+ readiness score</p>
            </div>
            <div>
              <Label>Exceptional Bonus (90%+ Readiness)</Label>
              <Input
                type="number"
                value={formData.cpd_points_coaching_exceptional}
                onChange={(e) => updateField('cpd_points_coaching_exceptional', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-muted-foreground mt-1">Bonus for 90%+ readiness score</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Streak Bonuses */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-5 w-5" />
                Streak Bonuses
              </CardTitle>
              <CardDescription>Reward consistent daily practice</CardDescription>
            </div>
            <Switch
              checked={formData.streak_bonus_enabled}
              onCheckedChange={(checked) => updateField('streak_bonus_enabled', checked)}
            />
          </div>
        </CardHeader>
        {formData.streak_bonus_enabled && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label>7-Day Streak Bonus</Label>
                <Input
                  type="number"
                  value={formData.streak_7_day_bonus}
                  onChange={(e) => updateField('streak_7_day_bonus', parseInt(e.target.value) || 0)}
                  min={0}
                />
              </div>
              <div>
                <Label>14-Day Streak Bonus</Label>
                <Input
                  type="number"
                  value={formData.streak_14_day_bonus}
                  onChange={(e) => updateField('streak_14_day_bonus', parseInt(e.target.value) || 0)}
                  min={0}
                />
              </div>
              <div>
                <Label>30-Day Streak Bonus</Label>
                <Input
                  type="number"
                  value={formData.streak_30_day_bonus}
                  onChange={(e) => updateField('streak_30_day_bonus', parseInt(e.target.value) || 0)}
                  min={0}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Feature Toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Feature Toggles
          </CardTitle>
          <CardDescription>Enable or disable gamification features</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Leaderboard</Label>
              <p className="text-sm text-muted-foreground">Show ranking by CPD points</p>
            </div>
            <Switch
              checked={formData.leaderboard_enabled}
              onCheckedChange={(checked) => updateField('leaderboard_enabled', checked)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Badges</Label>
              <p className="text-sm text-muted-foreground">Award achievement badges</p>
            </div>
            <Switch
              checked={formData.badges_enabled}
              onCheckedChange={(checked) => updateField('badges_enabled', checked)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Milestones</Label>
              <p className="text-sm text-muted-foreground">Track major achievements</p>
            </div>
            <Switch
              checked={formData.milestones_enabled}
              onCheckedChange={(checked) => updateField('milestones_enabled', checked)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Rewards Store</Label>
              <p className="text-sm text-muted-foreground">Allow point redemption for prizes</p>
            </div>
            <Switch
              checked={formData.rewards_enabled}
              onCheckedChange={(checked) => updateField('rewards_enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
